import { buildScheduledLessons } from './studentSchedule';

export const STUDENT_STATUS_META = {
  available: {
    label: '',
    tone: 'accent',
    helper: '',
    ctaLabel: 'View course',
    hint: '',
  },
  request_submitted: {
    label: '',
    tone: 'soft',
    helper: '',
    ctaLabel: 'View course',
    hint: '',
  },
  payment_pending: {
    label: '',
    tone: 'warning',
    helper: '',
    ctaLabel: 'View course',
    hint: '',
  },
  payment_review: {
    label: '',
    tone: 'soft',
    helper: '',
    ctaLabel: 'View course',
    hint: '',
  },
  active: {
    label: '',
    tone: 'success',
    helper: '',
    ctaLabel: 'Open course',
    hint: '',
  },
  completed: {
    label: '',
    tone: 'success',
    helper: '',
    ctaLabel: 'View course',
    hint: '',
  },
};

export const getStudentStatusMeta = (status) =>
  STUDENT_STATUS_META[status] || STUDENT_STATUS_META.available;

export const getStudentCourseAction = (course) => {
  const meta = getStudentStatusMeta(course?.status);
  switch (course?.status) {
    case 'active':
      return {
        label: meta.ctaLabel,
        to: `/courses/my-courses/${course.id}`,
        hint: meta.hint,
      };
    default:
      return {
        label: 'View course',
        to: `/courses/${course.id}`,
        hint: '',
      };
  }
};

export const formatStudentCurrency = (amount) =>
  `LKR ${Number(amount || 0).toLocaleString()}`;

export const formatStudentDate = (value, fallback = 'Not set') =>
  value
    ? new Date(value).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : fallback;

export const getCourseProgressMessage = (progress) => {
  const value = Number(progress || 0);
  if (value >= 100) return 'Course finished';
  if (value > 0) return `${value}% complete`;
  return 'Open course details';
};

export const buildStudentProfileSummary = (user, student) => {
  const personal = student?.personalDetails || {};
  const documents = student?.documents || {};
  const checklist = [
    {
      key: 'name',
      label: 'Name',
      completed: Boolean(personal.firstName || user?.firstName),
    },
    {
      key: 'phone',
      label: 'Phone',
      completed: Boolean(personal.phone || user?.phone),
    },
    {
      key: 'address',
      label: 'Address',
      completed: Boolean(personal.address || user?.address),
    },
    {
      key: 'photo',
      label: 'Photo',
      completed: Boolean(documents.photoURL || user?.photoURL),
    },
  ];

  const completedCount = checklist.filter((item) => item.completed).length;

  return {
    checklist,
    completedCount,
    totalCount: checklist.length,
    completion: Math.round((completedCount / checklist.length) * 100),
  };
};

export const getStudentDashboardState = ({
  activeCourses = [],
  paymentRequests = [],
  payments = [],
  profileSummary,
}) => {
  if (!activeCourses.length) {
    if (paymentRequests.some((item) => item.status === 'pending')) {
      return 'pending_payment';
    }
    if (payments.some((item) => item.status === 'pending')) {
      return 'pending_approval';
    }
    return 'new';
  }

  if (paymentRequests.some((item) => item.status === 'pending')) {
    return 'active_with_payment';
  }

  if ((profileSummary?.completion || 0) < 100) {
    return 'active_profile_needed';
  }

  return 'active';
};

export const buildStudentTaskList = ({
  state,
  profileSummary,
  paymentRequests = [],
  payments = [],
  activeCourses = [],
  nextLesson,
}) => {
  const tasks = [];

  if ((profileSummary?.completion || 0) < 100) {
    tasks.push({
      key: 'profile',
      title: 'Complete profile',
      body: `${profileSummary.completedCount}/${profileSummary.totalCount} essentials done`,
      to: '/profile',
      action: 'Open profile',
      tone: 'accent',
    });
  }

  if (paymentRequests.some((item) => item.status === 'pending')) {
    const request = paymentRequests.find((item) => item.status === 'pending');
    tasks.push({
      key: 'payment',
      title: 'Registration fee',
      body: formatStudentCurrency(request?.amount),
      to: '/payments',
      action: 'Open payments',
      tone: 'warning',
    });
  }

  if (payments.some((item) => item.status === 'pending')) {
    tasks.push({
      key: 'review',
      title: 'Receipt sent',
      body: 'Waiting for review',
      to: '/payments',
      action: 'View payment',
      tone: 'soft',
    });
  }

  if (state === 'new') {
    tasks.unshift({
      key: 'discover',
      title: 'Choose a course',
      body: 'Open course details to begin',
      to: '/courses/available',
      action: 'View courses',
      tone: 'accent',
    });
  }

  if (nextLesson) {
    tasks.push({
      key: 'next-lesson',
      title: 'Next class',
      body: `${nextLesson.courseName} on ${formatStudentDate(nextLesson.date)}`,
      to: `/courses/my-courses/${nextLesson.courseId}`,
      action: 'Open course',
      tone: 'success',
    });
  }

  if (activeCourses.length) {
    tasks.push({
      key: 'continue',
      title: activeCourses[0].name,
      body: `${Number(activeCourses[0].progress || 0)}% complete`,
      to: `/courses/my-courses/${activeCourses[0].id}`,
      action: 'Open course',
      tone: 'success',
    });
  }

  return tasks.slice(0, 4);
};

export const getNextStudentLesson = (activeCourses = []) => {
  const upcoming = [];

  activeCourses.forEach((course) => {
    buildScheduledLessons({
      course,
      enrollment: course.enrollment,
    }).forEach((lesson) => {
      if (!lesson.scheduledDate || !lesson.isUpcoming) return;
      upcoming.push({
        courseId: course.id,
        courseName: course.name,
        title: lesson.title || `Lesson ${lesson.lessonIndex + 1}`,
        date: lesson.scheduledDate.toISOString(),
      });
    });
  });

  upcoming.sort((a, b) => new Date(a.date) - new Date(b.date));
  return upcoming[0] || null;
};
